import BlogSection from "../atoms/BlogPreview";
import ContactForm from "../atoms/ContactForm";
import ServicesHeader from "../atoms/ServicesHeader";
import Button from "../components/v2/Button";
import Input from "../components/v2/Input";
import Textarea from "../components/v2/TextArea";
import Typography from "../components/v2/Typography";
import useApp from "../hooks/useApp";

const DomainBroker = () => {

    const appCtx = useApp();

    return (
        <>
            <ServicesHeader active="/services/domain-brokering" />
            {/* <!-- 1. HERO --> */}
            <section className="py-20 md:py-28 bg-white brokering-service">
                <div className="container mx-auto px-4 max-w-6xl grid md:grid-cols-2 gap-12 items-start">
                    <div>
                        <h1 className="text-5xl font-bold leading-tight mb-6">Premium Domain Brokering</h1>
                        <p className="text-xl mb-4">Eight‑figure closed sales · 100% confidential · 24/7 direct access</p>
                        <p className="text-lg mb-6 max-w-xl">We've placed premium names such as <em>Cybersecurity.com</em>, <em>NEON.com</em>, and <em>CTO.com</em> with world‑class brands—let us unlock the full value of yours.</p>
                        <ul className="list-disc list-inside space-y-2 text-lg">
                            <li>Stealth outreach to high‑intent, qualified buyers</li>
                            <li>Experienced negotiators from corporate & cybersecurity registrars</li>
                            <li>Zero upfront costs — commissions only on success</li>
                        </ul>
                    </div>
                    {/* Callback Form */}
                    <div className="border-1 border-blue-500 p-8 shadow-sm bg-gray-50">
                        <h2 className="text-2xl font-semibold mb-4">Get a Free Valuation</h2>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            console.log(e.target);
                            appCtx.submitForm(e.target.name.value, e.target.email.value, e.target.phone.value, e.target.query.value);
                        }} className="space-y-4">
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Name</Typography>
                                </div>
                                <Input isDark={true} name="name" label="" placeholder="John Doe" type="text" value="" onChange={() => { }} />
                            </div>
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Phone number</Typography>
                                </div>
                                <Input isDark={true} name="phone" label="" placeholder="+1 123456789" type="number" value="" onChange={() => { }} />
                            </div>
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Email</Typography>
                                </div>
                                <Input isDark={true} name="email" label="" placeholder="johndoe@gmail.com" type="email" value="" onChange={() => { }} />
                            </div>
                            <Textarea isDark={true} name="query" label="" value="" onChange={() => { }} style={{height: 170}} />
                            <Button variant="primary" type="submit" fullWidth>
                                <Typography variant="button">Submit</Typography>
                            </Button>
                        </form>
                        <p className="text-sm mt-2">*Valuations offered at our discretion for domains that meet eligibility criteria.</p>
                    </div>
                </div>
            </section>

            {/* 2. WHY BROKER WITH US */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Why Broker Your Domain with NameConnect?</h2>
                    <ul className="grid md:grid-cols-2 gap-6 text-lg list-disc list-inside">
                        <li>Stealth outreach to high‑intent, qualified buyers</li>
                        <li>Experienced negotiators from corporate & cybersecurity registrars</li>
                        <li>Zero upfront costs — commissions only on success</li>
                        <li>Free escrow & transfer concierge handled end‑to‑end</li>
                        <li>Transparent communication and legal protection throughout the deal</li>
                    </ul>
                </div>
            </section>

            {/* 3. ELIGIBILITY */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Is Your Domain Eligible?</h2>
                    <p className="text-lg mb-4">We specialise in high‑value assets and accept domains that meet <strong>at least two</strong> of the following:</p>
                    <ul className="space-y-4 text-lg list-disc list-inside">
                        <li>One‑word .com or two‑word brandable .com (<em>e.g., Galaxy.com, SolarAI.com</em>)</li>
                        <li>Exact‑match keyword with clear commercial intent</li>
                        <li>Documented offers above $50k</li>
                        <li>Pronounceable brandable variants (4–6 letters) with clean trademark status</li>
                    </ul>
                    <p className="text-sm text-gray-500 mt-4">Not sure? Submit for a complimentary review—our team replies within 24h.</p>
                </div>
            </section>

            {/* 4. PROCESS */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-6xl">
                    <h2 className="text-3xl font-semibold mb-8">Our 5‑Step Brokering Framework</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8 text-lg order-2 lg:order-1">
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">1. Consult & Valuation</h3>
                                <p>Data‑driven appraisal and pricing strategy.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">2. Target Buyer Mapping</h3>
                                <p>A shortlist of strategic buyers & investors.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">3. Stealth Outreach</h3>
                                <p>Confidential, personalised pitches—no public listings.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">4. Negotiation & Agreement</h3>
                                <p>We secure optimal terms and handle contracts.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">5. Free Escrow & Transfer</h3>
                                <p>NameConnect covers escrow fees and oversees flawless transfer into the buyer's account.</p>
                            </div>
                        </div>
                        <div className="flex justify-center lg:justify-end order-1 lg:order-2">
                            <img 
                                src="https://cdn.webstadia.com/3c886f95-12b5-4107-a816-e5e4a024471e.jpeg" 
                                alt="5-Step Brokering Framework" 
                                className="w-full max-w-md lg:max-w-lg rounded-lg shadow-lg"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. NOTABLE SALES */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Select Sales Highlights</h2>
                    <ul className="grid md:grid-cols-2 gap-4 text-lg list-disc list-inside">
                        <li>Cybersecurity.com – 7‑figure sale to Nasdaq security firm</li>
                        <li>NEON.com – FinTech rebrand acquisition, closed in 14 days</li>
                        <li>League.com – Sports‑tech Series B, 11‑day turnaround</li>
                        <li>Shield.com – Enterprise security brand upgrade</li>
                        <li>CTO.com – Talent‑platform strategic purchase</li>
                    </ul>
                </div>
            </section>

            {/* 6. FAQ */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Frequently Asked Questions</h2>
                    <div className="space-y-6">
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">How much does it cost to list?</summary>
                            <p className="pt-2">$0 upfront. We only charge our 15% success fee (min $2,500) once the buyer's funds have cleared escrow.</p>
                        </details>
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">Who pays escrow & transfer fees?</summary>
                            <p className="pt-2">NameConnect covers standard Escrow.com fees and provides free transfer concierge, so you keep more of the final sale price.</p>
                        </details>
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">Will my identity remain private?</summary>
                            <p className="pt-2">Yes. All outreach uses proxy emails and NDAs. We never disclose your details without written permission.</p>
                        </details>
                    </div>
                </div>
            </section>

            {/* 7. CTA */}
            <section className="py-20 bg-blue-600 text-white text-center">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-4xl font-bold mb-4">Ready to Monetise Your Premium Domain?</h2>
                    <p className="text-lg mb-8">Submit your domain for a complimentary review. If it meets our criteria, we'll provide a free, data‑driven valuation and bespoke go‑to‑market plan.</p>
                    <a href="/contact" className="inline-block bg-white text-blue-600 font-semibold py-4 px-10 rounded">Submit My Domain</a>
                </div>
            </section>

            <BlogSection />
        </>
    );
};

export default DomainBroker;